import React from 'react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { MultiSelectCombobox } from 'components/ui/multi-select-combobox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { User, Role, CreateUserRequest, UpdateUserRequest, listRoles, getRoleOUOptions, AccessOU, UserDropdownItem, listUsersDropdown, Division, listDivisions, KeyAccount, listKeyAccounts } from './api';

type UserFormState = {
  ecode: string;
  full_name: string;
  email: string;
  mobile_no: string;
  role_id: number | null;
  reporting_manager_ecode: string | null;
  division_ou_code: string | null;
  access_ou_codes: string[];
  key_accounts: string[];
};

function validate(values: UserFormState, ouOptions: AccessOU[]): Partial<Record<keyof UserFormState, string>> {
  const errors: Partial<Record<keyof UserFormState, string>> = {};
  if (!values.ecode || !/^.{5}$/.test(values.ecode)) {
    errors.ecode = 'Ecode must 5 characters';
  }
  if (!values.full_name || values.full_name.trim().length === 0) {
    errors.full_name = 'Name is required';
  }
  if (!values.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    errors.email = 'Enter a valid email';
  }
  if (!values.mobile_no || !/^\d{10}$/.test(values.mobile_no)) {
    errors.mobile_no = 'Enter a 10-digit phone number';
  }
  if (!values.role_id) {
    errors.role_id = 'Role is required';
  }
  if (!values.division_ou_code) {
    errors.division_ou_code = 'Division is required';
  }
  if (ouOptions.length > 0 && values.access_ou_codes.length === 0) {
    errors.access_ou_codes = 'At least one access area must be selected';
  }
  return errors;
}

interface UserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onSubmit: (data: CreateUserRequest | UpdateUserRequest, isEdit: boolean) => Promise<void>;
}

const UserModal: React.FC<UserModalProps> = ({ open, onOpenChange, user, onSubmit }) => {
  const isEdit = !!user;
  const [form, setForm] = React.useState<UserFormState>({
    ecode: '',
    full_name: '',
    email: '',
    mobile_no: '',
    role_id: null,
    reporting_manager_ecode: null,
    division_ou_code: null,
    access_ou_codes: [],
    key_accounts: []
  });
  const [formErrors, setFormErrors] = React.useState<Partial<Record<keyof UserFormState, string>>>({});
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [submitError, setSubmitError] = React.useState<string>('');

  // Role and OU options
  const [roles, setRoles] = React.useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = React.useState<boolean>(false);
  const [divisions, setDivisions] = React.useState<Division[]>([]);
  const [divisionsLoading, setDivisionsLoading] = React.useState<boolean>(false);
  const [ouOptions, setOuOptions] = React.useState<AccessOU[]>([]);
  const [ouOptionsLoading, setOuOptionsLoading] = React.useState<boolean>(false);
  const [managerOptions, setManagerOptions] = React.useState<UserDropdownItem[]>([]);
  const [managersLoading, setManagersLoading] = React.useState<boolean>(false);
  const [keyAccounts, setKeyAccounts] = React.useState<KeyAccount[]>([]);
  const [keyAccountsLoading, setKeyAccountsLoading] = React.useState(false);

  // Fetch roles when modal opens
  React.useEffect(() => {
    if (open) {
      fetchRoles();
      fetchDivisions();
      fetchManagers();
      fetchKeyAccounts();
      if (user) {
        setForm({
          ecode: user.ecode,
          full_name: user.full_name,
          email: user.email,
          mobile_no: user.mobile_no,
          role_id: user.role_id,
          reporting_manager_ecode: user.reporting_manager_ecode,
          division_ou_code: user.division_ou_code,
          access_ou_codes: user.access_ou_codes,
          key_accounts: user.key_accounts ?? [],
        });
        // Fetch OU options for the user's role (don't clear existing selection)
        if (user.role_id) {
          fetchOUOptions(user.role_id, false);
        }
      } else {
        setForm({
          ecode: '',
          full_name: '',
          email: '',
          mobile_no: '',
          role_id: null,
          reporting_manager_ecode: null,
          division_ou_code: null,
          access_ou_codes: [], 
          key_accounts: []
        });
        setOuOptions([]);
      }
      setFormErrors({});
      setSubmitError('');
    }
  }, [open, user]);

  const fetchRoles = async () => {
    setRolesLoading(true);
    try {
      const response = await listRoles();
      setRoles(response.items || []);
    } catch (err: unknown) {
      console.error('Failed to fetch roles:', err);
    } finally {
      setRolesLoading(false);
    }
  };

  const fetchDivisions = async () => {
    setDivisionsLoading(true);
    try {
      const response = await listDivisions();
      setDivisions(response.items || []);
    } catch (err: unknown) {
      console.error('Failed to fetch divisions:', err);
    } finally {
      setDivisionsLoading(false);
    }
  };

  const fetchManagers = async () => {
    setManagersLoading(true);
    try {
      const response = await listUsersDropdown();
      setManagerOptions(response.items || []);
    } catch (err: unknown) {
      console.error('Failed to fetch managers:', err);
    } finally {
      setManagersLoading(false);
    }
  };

  const fetchOUOptions = async (roleId: number, clearSelection = true) => {
    setOuOptionsLoading(true);
    try {
      const response = await getRoleOUOptions(roleId);
      setOuOptions(response.items || []);
      // clear selected OUs when role changes (but not during initial load)
      if (clearSelection) {
        setForm(prev => ({ ...prev, access_ou_codes: [] }));
      }
    } catch (err: unknown) {
      console.error('Failed to fetch OU options:', err);
      setOuOptions([]);
      // clear OUs on error
      setForm(prev => ({ ...prev, access_ou_codes: [] }));
    } finally {
      setOuOptionsLoading(false);
    }
  };

  const fetchKeyAccounts = async () => {
    setKeyAccountsLoading(true);
    try {
      const resp = await listKeyAccounts();
      setKeyAccounts(resp.items || []);
    } catch (err) {
      console.error('Failed to fetch key accounts', err);
      setKeyAccounts([]);
    } finally {
      setKeyAccountsLoading(false);
    }
  };

  const onChangeField = (field: keyof UserFormState, value: string | number | string[] | null) => {
    setForm(prev => ({ ...prev, [field]: value }));

    // When role changes, fetch OU options
    if (field === 'role_id' && typeof value === 'number') {
      fetchOUOptions(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate(form, ouOptions);
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    setSubmitError('');
    try {
      if (isEdit) {
        const { ecode, ...updateData } = form;
        await onSubmit(updateData as UpdateUserRequest, true);
      } else {
        await onSubmit(form as CreateUserRequest, false);
      }
      onOpenChange(false);
    } catch (e: unknown) {
      setSubmitError(e instanceof Error ? e.message : 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const removeKeyAccount = (custCode: string) => {
    setForm(prev => ({
      ...prev,
      key_accounts: prev.key_accounts.filter(code => code !== custCode),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit User' : 'Create User'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Ecode
            </label>
            <Input
              value={form.ecode}
              onChange={(e) => onChangeField('ecode', e.target.value.toUpperCase())}
              placeholder="L0000"
              disabled={isEdit}
            />
            {formErrors.ecode && <p className="text-sm font-medium text-destructive">{formErrors.ecode}</p>}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Full Name
            </label>
            <Input
              value={form.full_name}
              onChange={(e) => onChangeField('full_name', e.target.value)}
              placeholder="Jane Doe"
            />
            {formErrors.full_name && <p className="text-sm font-medium text-destructive">{formErrors.full_name}</p>}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Email
            </label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => onChangeField('email', e.target.value)}
              placeholder="jane@example.com"
            />
            {formErrors.email && <p className="text-sm font-medium text-destructive">{formErrors.email}</p>}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Mobile No
            </label>
            <Input
              value={form.mobile_no}
              onChange={(e) => onChangeField('mobile_no', e.target.value)}
              placeholder="9876543210"
            />
            {formErrors.mobile_no && <p className="text-sm font-medium text-destructive">{formErrors.mobile_no}</p>}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Division *
            </label>
            <Select
              value={form.division_ou_code || ''}
              onValueChange={(value) => onChangeField('division_ou_code', value)}
              disabled={divisionsLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select division" />
              </SelectTrigger>
              <SelectContent>
                {divisions.map((division) => (
                  <SelectItem key={division.ou_code} value={division.ou_code}>
                    {division.ou_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formErrors.division_ou_code && <p className="text-sm font-medium text-destructive">{formErrors.division_ou_code}</p>}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Role *
            </label>
            <Select
              value={form.role_id ? String(form.role_id) : ''}
              onValueChange={(value) => onChangeField('role_id', Number(value))}
              disabled={rolesLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.role_id} value={String(role.role_id)}>
                    {role.role_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formErrors.role_id && <p className="text-sm font-medium text-destructive">{formErrors.role_id}</p>}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Reporting Manager
            </label>
            <Select
              value={form.reporting_manager_ecode || '__none__'}
              onValueChange={(value) => onChangeField('reporting_manager_ecode', value === '__none__' ? null : value)}
              disabled={managersLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select reporting manager" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {managerOptions.filter(m => m.ecode !== form.ecode).map((manager) => (
                  <SelectItem key={manager.ecode} value={manager.ecode}>
                    {manager.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Key Accounts
            </label>

            {keyAccountsLoading ? (
              <div className="flex items-center justify-center h-10 border rounded-md">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                <MultiSelectCombobox
                  options={keyAccounts.map(ka => ({
                    value: ka.cust_code,
                    label: ka.cust_name,
                  }))}
                  value={form.key_accounts}
                  onValueChange={(value) => onChangeField('key_accounts', value)}
                  placeholder="Select key accounts"
                  searchPlaceholder="Search customers..."
                  emptyText="No customers found"
                />

                {form.key_accounts.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.key_accounts.map(code => {
                      const account = keyAccounts.find(k => k.cust_code === code);
                      return (
                        <span
                          key={code}
                          className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm"
                        >
                          {account?.cust_name ?? code}
                          <button
                            type="button"
                            onClick={() => removeKeyAccount(code)}
                            className="ml-1 text-muted-foreground hover:text-destructive"
                          >
                            ✕
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
          {form.role_id && (
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Access Areas {ouOptions.length > 0 && '*'}
              </label>
              {ouOptionsLoading ? (
                <div className="flex items-center justify-center h-10 border rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                </div>
              ) : ouOptions.length === 0 ? (
                <div className="p-3 rounded-md bg-muted text-sm text-muted-foreground">
                  All access - no specific areas to select
                </div>
              ) : (
                <MultiSelectCombobox
                  options={ouOptions.map(ou => ({ value: ou.ou_code, label: ou.ou_name }))}
                  value={form.access_ou_codes}
                  onValueChange={(value) => onChangeField('access_ou_codes', value)}
                  placeholder="Select access areas"
                  searchPlaceholder="Search areas..."
                  emptyText="No areas found"
                />
              )}
              {formErrors.access_ou_codes && <p className="text-sm font-medium text-destructive">{formErrors.access_ou_codes}</p>}
            </div>
          )}
          {submitError && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {submitError}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserModal;
